import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PrinterIcon, EnvelopeIcon, ArrowLeftIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import Modal from './Modal';
import Button from './Button';
import Input from './Input';
import LoadingSpinner from './LoadingSpinner';
import { loadsAPI } from '../services/api';
import { formatWeight, formatDateTime, getTransactionTypeDisplay } from '../utils/helpers';
import { getLoadClientDisplayName } from '../utils/loadClientDisplay';
import { buildLoadWeighPassListing } from '../utils/loadWeighPasses';
import { extractTsMetaFromNotes, stripTsMetaFromNotes, getSupplementaryTaxOption } from '../utils/supplementaryTax';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useMailing } from '../contexts/MailingContext';
import { useAsyncOperation } from '../hooks/useApi';
import { useTarifConfig } from '../hooks/useTarifConfig';
import { computeLoadBillAmountPreview, loadTariffInstant } from '../utils/billAmountPreview';

export function getLoadStatusBadgeColor(status) {
  switch (status) {
    case 'REQUESTED': return 'bg-yellow-100 text-yellow-800';
    case 'REJECTED': return 'bg-red-100 text-red-800';
    case 'ACCEPTED': return 'bg-blue-100 text-blue-800';
    case 'LOADED': return 'bg-purple-100 text-purple-800';
    case 'DELIVERED': return 'bg-green-100 text-green-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

/** Name + optional phone for modal display; prefers Driver entity, then User driver, then truck assignments, then legacy truck fields. */
export function getLoadDriverDisplayLine(load) {
  if (!load) return null;
  const fromParts = (first, last, phone) => {
    const name = `${first || ''} ${last || ''}`.trim();
    if (!name) return null;
    const tel = phone != null && String(phone).trim() ? String(phone).trim() : '';
    return { name, phone: tel };
  };
  const de = load.driverEntity;
  if (de) {
    const line = fromParts(de.firstName, de.lastName, de.phone);
    if (line) return line;
  }
  const userDriver = load.driver;
  if (userDriver) {
    const line = fromParts(userDriver.firstName, userDriver.lastName, userDriver.phone);
    if (line) return line;
  }
  const truckDriver = load.truck?.truckDrivers?.[0]?.driver;
  if (truckDriver) {
    const line = fromParts(truckDriver.firstName, truckDriver.lastName, truckDriver.phone);
    if (line) return line;
  }
  const assigned = load.truck?.assignedDrivers?.[0]?.driver;
  if (assigned) {
    const line = fromParts(assigned.firstName, assigned.lastName, assigned.phone);
    if (line) return line;
  }
  const legacyName = load.truck?.driverName != null ? String(load.truck.driverName).trim() : '';
  if (legacyName) {
    const tel = load.truck?.driverPhone != null ? String(load.truck.driverPhone).trim() : '';
    return { name: legacyName, phone: tel };
  }
  return null;
}

/**
 * Read-only pesage / load details (same content as Loads page). Ticket print & email when enabled.
 */
const LoadDetailViewModal = ({
  isOpen,
  onClose,
  load,
  loading = false,
  onLoadUpdated,
  returnNavigation = null
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { mailingEnabled } = useMailing();
  const { isAdmin, isManagerOrAdmin, isManager, isDriver, isPeseur } = useAuth();
  const { execute: executeAsync, loading: actionLoading } = useAsyncOperation();
  const { tarifConfig, holidayDateSet } = useTarifConfig();
  const [currentLoad, setCurrentLoad] = useState(load);
  const [taxesAuPoidsExpanded, setTaxesAuPoidsExpanded] = useState(false);

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailTargetLoad, setEmailTargetLoad] = useState(null);

  useEffect(() => {
    if (!mailingEnabled) setEmailModalOpen(false);
  }, [mailingEnabled]);

  useEffect(() => {
    setCurrentLoad(load);
  }, [load]);

  useEffect(() => {
    if (!isOpen) {
      setEmailModalOpen(false);
      setEmailAddress('');
      setEmailTargetLoad(null);
      setTaxesAuPoidsExpanded(false);
    }
  }, [isOpen]);

  const billingPreview = useMemo(() => {
    const netKg = Number(currentLoad?.netWeight);
    if (!Number.isFinite(netKg) || netKg <= 0) return null;
    return computeLoadBillAmountPreview({
      netWeightKg: netKg,
      tariffInstant: loadTariffInstant(currentLoad),
      notes: currentLoad?.notes,
      amountTsFromLoad: currentLoad?.amountTs,
      tarifPoids: tarifConfig?.tarifPoids,
      tvaRate: tarifConfig?.tvaRate,
      holidayDateSet
    });
  }, [currentLoad, tarifConfig, holidayDateSet]);

  const displayAmountTtc = useMemo(() => {
    const fromServer = Number(currentLoad?.amountTtc);
    if (Number.isFinite(fromServer) && fromServer >= 0) return fromServer;
    return billingPreview?.amountTtc ?? null;
  }, [currentLoad?.amountTtc, billingPreview?.amountTtc]);

  const displayAmountTs = useMemo(() => {
    const fromServer = Number(currentLoad?.amountTs);
    if (Number.isFinite(fromServer) && fromServer > 0) return fromServer;
    return billingPreview?.amountTs ?? 0;
  }, [currentLoad?.amountTs, billingPreview?.amountTs]);

  const weighPassRows = useMemo(() => buildLoadWeighPassListing(currentLoad, t), [currentLoad, t]);
  const driverLine = useMemo(() => getLoadDriverDisplayLine(currentLoad), [currentLoad]);

  const handleOpenRelatedCertificate = (certificateNumber) => {
    if (!certificateNumber) return;
    onClose();
    navigate('/certificates', { state: { openCertificateNumber: certificateNumber } });
  };

  const handleReturnNavigation = () => {
    const certId = returnNavigation?.openCertificateId;
    const billId = returnNavigation?.openBillId;
    onClose();
    if (certId) {
      navigate('/certificates', { state: { openCertificateId: certId } });
      return;
    }
    if (billId) {
      navigate('/bills', { state: { openBillId: billId } });
    }
  };

  const showReturnNavigation =
    Boolean(returnNavigation?.openCertificateId || returnNavigation?.openBillId);

  const handleOpenRelatedDdp = () => {
    const ddpId = currentLoad?.ddp?.id;
    if (!ddpId) return;
    onClose();
    navigate('/ddp/list', { state: { openDdpId: ddpId } });
  };

  const peseurRecord = currentLoad?.peseur || null;
  const peseurId = String(currentLoad?.peseurId || peseurRecord?.id || '').trim();
  const peseurDisplayName = peseurRecord
    ? `${peseurRecord.firstName || ''} ${peseurRecord.lastName || ''}`.trim() ||
      peseurRecord.username ||
      peseurId
    : '';

  const handleOpenPeseurLoads = () => {
    if (!peseurId || !isManager()) return;
    onClose();
    navigate(`/loads?peseurId=${encodeURIComponent(peseurId)}`);
  };

  const canApproveRequest =
    ['REQUESTED', 'REJECTED'].includes(String(currentLoad?.status || '').toUpperCase()) &&
    ((isManagerOrAdmin() && !isDriver()) || isPeseur());
  const canRejectRequest =
    ['REQUESTED', 'ACCEPTED'].includes(String(currentLoad?.status || '').toUpperCase()) &&
    isManagerOrAdmin() &&
    !isDriver();

  const handleRequestStatusChange = async (targetStatus) => {
    if (!currentLoad?.id) return;
    await executeAsync(
      async () => {
        await loadsAPI.updateStatus(currentLoad.id, { status: targetStatus });
        const refreshed = await loadsAPI.getById(currentLoad.id);
        setCurrentLoad(refreshed?.data?.load || currentLoad);
        if (typeof onLoadUpdated === 'function') {
          onLoadUpdated(refreshed?.data?.load || currentLoad);
        }
      },
      {
        showSuccessToast: true,
        successMessage:
          targetStatus === 'ACCEPTED'
            ? t('loads.actions.acceptLoad')
            : t('loads.actions.rejectLoad'),
      }
    );
  };

  const handlePrintTicket = async (loadToPrint) => {
    await executeAsync(
      async () => {
        const currentLanguage = localStorage.getItem('i18nextLng') || 'fr';
        const response = await loadsAPI.getTicketPDF(loadToPrint.id, currentLanguage);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const printWindow = window.open(url);
        printWindow.onload = () => {
          printWindow.print();
        };
      },
      {
        showSuccessToast: true,
        successMessage: t('loads.ticketGeneratedSuccess'),
      }
    );
  };

  const handleEmailTicket = (loadForEmail) => {
    setEmailTargetLoad(loadForEmail);
    setEmailAddress(loadForEmail.client?.contactEmail || loadForEmail.transporteur?.contactEmail || '');
    setEmailModalOpen(true);
  };

  const sendEmail = async () => {
    if (!emailTargetLoad?.id) return;
    if (!emailAddress.trim()) {
      toast.error(t('loads.enterEmailError'));
      return;
    }

    await executeAsync(
      async () => {
        const currentLanguage = localStorage.getItem('i18nextLng') || 'fr';
        await loadsAPI.sendTicket(emailTargetLoad.id, { email: emailAddress, lang: currentLanguage });
        setEmailModalOpen(false);
        setEmailAddress('');
        setEmailTargetLoad(null);
      },
      {
        showSuccessToast: true,
        successMessage: t('loads.ticketEmailSent'),
      }
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t('loads.modal.loadDetailsTitle')}
        size="lg"
      >
        {loading && !load ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : null}
        {currentLoad && (
          <div className="space-y-6">
            {showReturnNavigation && (
              <div className="flex flex-wrap gap-2">
                {returnNavigation?.openCertificateId && (
                  <Button type="button" variant="outline" size="sm" onClick={handleReturnNavigation}>
                    <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
                    {t('loads.modal.backToCertificate')}
                  </Button>
                )}
                {!returnNavigation?.openCertificateId && returnNavigation?.openBillId && (
                  <Button type="button" variant="outline" size="sm" onClick={handleReturnNavigation}>
                    <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
                    {t('loads.modal.backToBill')}
                  </Button>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">{t('loads.modal.basicInformation')}</h3>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('loads.modal.dateTime')}</label>
                    <p className="text-sm text-gray-900">{formatDateTime(currentLoad.loadDate)}</p>
                  </div>

                  {currentLoad.ticket && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t('loads.ticketNumber')}</label>
                      <p className="text-sm text-gray-900 font-mono">{currentLoad.ticket.ticketNumber}</p>
                    </div>
                  )}

                  {(Array.isArray(currentLoad.linkedCertificates) ? currentLoad.linkedCertificates.length > 0 : Boolean(currentLoad.certificateNumber)) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t('certificates.certificateNumber')}</label>
                      <div className="mt-1 space-y-2">
                        {(Array.isArray(currentLoad.linkedCertificates) && currentLoad.linkedCertificates.length > 0
                          ? currentLoad.linkedCertificates
                          : [{ certificateNumber: currentLoad.certificateNumber }]
                        )
                          .filter((c) => Boolean(c?.certificateNumber))
                          .map((cert, idx) => (
                            <div key={`${cert.certificateNumber}-${idx}`} className="flex items-center gap-2 flex-wrap">
                              {isDriver() ? (
                                <span className="text-sm font-mono bg-yellow-100 text-yellow-800 px-2 py-1 rounded inline-block">
                                  {cert.certificateNumber}
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  className="text-sm font-mono bg-yellow-100 text-yellow-800 px-2 py-1 rounded inline-block hover:bg-yellow-200 underline underline-offset-2"
                                  onClick={() => handleOpenRelatedCertificate(cert.certificateNumber)}
                                >
                                  {cert.certificateNumber}
                                </button>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {currentLoad.ddp && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t('loads.modal.ddpNumber')}</label>
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        {isDriver() ? (
                          <span className="text-sm text-gray-900 font-mono">
                            {currentLoad.ddp.manutentionNumber}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="text-sm text-gray-900 font-mono underline underline-offset-2 hover:text-blue-700"
                            onClick={handleOpenRelatedDdp}
                          >
                            {currentLoad.ddp.manutentionNumber}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('loads.modal.client')}</label>
                    <p className="text-sm text-gray-900">
                      {getLoadClientDisplayName(currentLoad) || '—'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('loads.modal.product')}</label>
                    <p className="text-sm text-gray-900">{currentLoad.product?.name}</p>
                  </div>

                  {!isAdmin() && !isManagerOrAdmin() && !isDriver() && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t('loads.modal.transactionType')}</label>
                      <p className="text-sm text-gray-900">{getTransactionTypeDisplay(currentLoad.transactionType)}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('loads.status')}</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLoadStatusBadgeColor(currentLoad.status)}`}>
                      {t(`loads.statuses.${currentLoad.status?.toLowerCase()}`)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">{t('loads.modal.truckAndLocation')}</h3>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('loads.modal.truck')}</label>
                    <p className="text-sm text-gray-900">{currentLoad.truck?.plateNumber}</p>
                  </div>

                  {driverLine && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t('loads.modal.driver')}</label>
                      <p className="text-sm text-gray-900">
                        {driverLine.name}
                        {driverLine.phone ? ` (${driverLine.phone})` : ''}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('loads.modal.port')}</label>
                    <p className="text-sm text-gray-900">{currentLoad.port?.name}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('loads.modal.weighbridge')}</label>
                    <p className="text-sm text-gray-900">{currentLoad.weighbridge?.name}</p>
                  </div>

                  {peseurId ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t('loads.modal.peseur')}</label>
                      {isManager() ? (
                        <button
                          type="button"
                          className="text-sm text-blue-700 font-medium underline underline-offset-2 hover:text-blue-900 text-left"
                          onClick={handleOpenPeseurLoads}
                        >
                          {peseurDisplayName || '—'}
                        </button>
                      ) : (
                        <p className="text-sm text-gray-900">{peseurDisplayName || '—'}</p>
                      )}
                      {isManager() && (
                        <p className="text-xs text-gray-500 mt-0.5">{t('loads.modal.viewPeseurLoadsHint')}</p>
                      )}
                    </div>
                  ) : null}
                  {currentLoad.operator &&
                    String(currentLoad.operator.id || '') !== peseurId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t('loads.modal.requestedBy')}</label>
                      <p className="text-sm text-gray-900">
                        {currentLoad.operator.firstName} {currentLoad.operator.lastName}
                        {currentLoad.operator.username ? ` (@${currentLoad.operator.username})` : ''}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('loads.modal.weightInformation')}</h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                {weighPassRows.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      {t('loads.modal.passListingTitle')}
                    </p>
                    <table className="w-full text-sm border border-gray-200 rounded-md overflow-hidden bg-white">
                      <thead className="bg-gray-100 text-xs text-gray-600">
                        <tr>
                          <th className="text-left font-semibold px-3 py-2 border-b border-gray-200">
                            {t('loads.modal.passTableColPass')}
                          </th>
                          <th className="text-right font-semibold px-3 py-2 border-b border-gray-200 whitespace-nowrap">
                            {t('loads.modal.passTableColWeight')}
                          </th>
                          <th className="text-right font-semibold px-3 py-2 border-b border-gray-200 whitespace-nowrap">
                            {t('loads.modal.dateTime')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {weighPassRows.map((row) => (
                          <tr key={row.key}>
                            <td className="px-3 py-2.5 font-medium text-gray-800 align-middle">{row.label}</td>
                            <td className="px-3 py-2.5 text-right font-semibold text-gray-900 tabular-nums align-middle whitespace-nowrap">
                              {row.weight}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-600 tabular-nums align-middle whitespace-nowrap text-xs sm:text-sm">
                              {row.date}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {weighPassRows.length > 0 && (
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {t('loads.modal.weightSummary')}
                  </p>
                )}
                <div className={`grid gap-4 ${!isAdmin() && !isManagerOrAdmin() ? 'grid-cols-3' : 'grid-cols-2'}`}>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">{t('loads.modal.emptyWeight')}</p>
                    <p className="text-lg font-semibold">
                      {currentLoad.emptyWeight
                        ? formatWeight(currentLoad.emptyWeight, 'ton', { noUnit: true })
                        : (currentLoad.truck?.emptyWeight
                          ? formatWeight(currentLoad.truck.emptyWeight, 'ton', { noUnit: true })
                          : '-')}
                    </p>
                    {currentLoad.truck?.emptyWeight && !currentLoad.emptyWeight && (
                      <p className="text-xs text-gray-500 mt-1">{t('loads.modal.fromTruckTare')}</p>
                    )}
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">{t('loads.modal.loadedWeight')}</p>
                    <p className="text-lg font-semibold">{formatWeight(currentLoad.loadedWeight, 'ton', { noUnit: true })}</p>
                  </div>
                  {!isAdmin() && !isManagerOrAdmin() && (
                    <div className="text-center">
                      <p className="text-sm text-gray-600">{t('loads.modal.netWeight')}</p>
                      <p className="text-xl font-bold text-blue-600">{formatWeight(currentLoad.netWeight, 'ton', { noUnit: true })}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {currentLoad.netWeight != null &&
              currentLoad.netWeight > 0 &&
              billingPreview &&
              displayAmountTtc != null &&
              Number.isFinite(Number(displayAmountTtc)) && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('loads.taxesAuPoids.totalTtc', 'Total TTC')}</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setTaxesAuPoidsExpanded((prev) => !prev)}
                    className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-blue-100/70 transition-colors"
                    aria-expanded={taxesAuPoidsExpanded}
                  >
                    <span className="text-sm font-medium text-blue-900">
                      {displayAmountTs > 0
                        ? t('loads.taxesAuPoids.amountTtcWithTs', 'Total TTC (Taxes Au Poids + TVA + TS)')
                        : t('loads.taxesAuPoids.amountTtc', 'Total TTC (Taxes Au Poids + TVA)')}
                      {' '}
                      <span className="font-bold">
                        {Number(displayAmountTtc).toLocaleString('fr-FR', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        })}{' '}
                        FCFA
                      </span>
                    </span>
                    {taxesAuPoidsExpanded ? (
                      <ChevronUpIcon className="h-5 w-5 text-blue-600 shrink-0" aria-hidden />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-blue-600 shrink-0" aria-hidden />
                    )}
                  </button>
                  {taxesAuPoidsExpanded && (
                    <div className="border-t border-blue-200 px-4 py-3">
                      <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.netWeight', 'Poids net')}</dt>
                          <dd className="text-blue-900 mt-1 font-semibold">
                            {billingPreview.netTon.toLocaleString('fr-FR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}{' '}
                            t
                          </dd>
                        </div>
                        <div>
                          <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.appliedTariff', 'Tarif appliqué')}</dt>
                          <dd className="text-blue-900 mt-1 font-semibold">
                            {billingPreview.isOuvrable
                              ? t('loads.taxesAuPoids.ouvrable', 'Ouvrable')
                              : t('loads.taxesAuPoids.special', 'Spécial (sam/dim/nuit/férié)')}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.rule', 'Règle de calcul')}</dt>
                          <dd className="text-blue-900 mt-1">
                            {billingPreview.netTon <= billingPreview.ceilingTon
                              ? `${t('loads.taxesAuPoids.minRule', '≤ plafond : montant minimum')} (${billingPreview.minHt.toLocaleString('fr-FR')} FCFA)`
                              : `${t('loads.taxesAuPoids.rateRule', '> plafond : tarif par tonne')} (${billingPreview.rateHtPerTon.toLocaleString('fr-FR')} FCFA/t)`}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.ceiling', 'Plafond')}</dt>
                          <dd className="text-blue-900 mt-1">
                            {billingPreview.ceilingTon.toLocaleString('fr-FR')} t
                          </dd>
                        </div>
                        <div>
                          <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.amountHt', 'Montant HT')}</dt>
                          <dd className="text-blue-900 mt-1 font-semibold">
                            {(Number.isFinite(Number(currentLoad.amountHtExclTva))
                              ? Number(currentLoad.amountHtExclTva)
                              : billingPreview.amountHt
                            ).toLocaleString('fr-FR')}{' '}
                            FCFA
                          </dd>
                        </div>
                        <div>
                          <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.amountTva', 'TVA')}</dt>
                          <dd className="text-blue-900 mt-1">
                            {(Number.isFinite(Number(currentLoad.amountTva))
                              ? Number(currentLoad.amountTva)
                              : billingPreview.amountTva
                            ).toLocaleString('fr-FR')}{' '}
                            FCFA
                          </dd>
                        </div>
                        {displayAmountTs > 0 && (
                          <div>
                            <dt className="text-blue-700 font-medium">{t('loads.ts.detail.title', 'Taxes Supplémentaires (TS)')}</dt>
                            <dd className="text-blue-900 mt-1">{displayAmountTs.toLocaleString('fr-FR')} FCFA</dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  )}
                </div>
              </div>
            )}

            {(() => {
              const tsMeta = currentLoad.status === 'LOADED' ? extractTsMetaFromNotes(currentLoad.notes) : null;
              const cleanNotes = stripTsMetaFromNotes(currentLoad.notes);
              const tsOption = tsMeta ? getSupplementaryTaxOption(tsMeta.optionCode) : null;
              return (
                <>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t('loads.ts.detail.title')}</h3>
                    {tsMeta ? (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <dl className="grid grid-cols-3 gap-4">
                          <div>
                            <dt className="text-xs text-amber-700 font-medium">{t('loads.ts.detail.option')}</dt>
                            <dd className="text-sm text-amber-900 mt-1">
                              {tsOption ? t(tsOption.labelKey) : tsMeta.description}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-amber-700 font-medium">{t('loads.ts.detail.hours')}</dt>
                            <dd className="text-sm text-amber-900 mt-1">{tsMeta.hours} h</dd>
                          </div>
                          <div>
                            <dt className="text-xs text-amber-700 font-medium">{t('loads.ts.detail.totalAmount')}</dt>
                            <dd className="text-sm font-semibold text-amber-900 mt-1">
                              {Number(tsMeta.totalAmount).toLocaleString()} FCFA
                            </dd>
                          </div>
                        </dl>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                        {currentLoad.status === 'LOADED' ? t('loads.ts.noSupplementaryTax') : t('loads.ts.tsSetWhenMarkedLoaded')}
                      </p>
                    )}
                  </div>
                  {cleanNotes && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">{t('loads.modal.notes')}</h3>
                      <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{cleanNotes}</p>
                    </div>
                  )}
                </>
              );
            })()}

            {(canApproveRequest || canRejectRequest || currentLoad?.ticket) && (
              <div className="flex justify-end space-x-3">
                {canApproveRequest && (
                  <Button
                    variant="outline"
                    className="text-blue-700 border-blue-300 bg-blue-50 hover:bg-blue-100"
                    onClick={() => handleRequestStatusChange('ACCEPTED')}
                    loading={actionLoading}
                  >
                    {t('loads.actions.acceptLoad')}
                  </Button>
                )}
                {canRejectRequest && (
                  <Button
                    variant="outline"
                    className="text-red-700 border-red-300 bg-red-50 hover:bg-red-100"
                    onClick={() => handleRequestStatusChange('REJECTED')}
                    loading={actionLoading}
                  >
                    {t('loads.actions.rejectLoad')}
                  </Button>
                )}
                {currentLoad?.ticket && (
                  <Button
                    variant="outline"
                    onClick={() => handlePrintTicket(currentLoad)}
                    loading={actionLoading}
                  >
                    <PrinterIcon className="h-4 w-4 mr-2" />
                    {t('loads.modal.printTicket')}
                  </Button>
                )}
                {currentLoad?.ticket && mailingEnabled && (
                  <Button
                    onClick={() => handleEmailTicket(currentLoad)}
                  >
                    <EnvelopeIcon className="h-4 w-4 mr-2" />
                    {t('loads.modal.emailTicket')}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        title={t('loads.modal.sendTicketTitle')}
        footer={
          <>
            <Button variant="outline" onClick={() => setEmailModalOpen(false)}>
              {t('loads.modal.cancel')}
            </Button>
            <Button onClick={sendEmail} loading={actionLoading}>
              {t('loads.modal.sendEmail')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            {t('loads.modal.sendTicketDescription', {
              loadId: emailTargetLoad?.ticket?.ticketNumber || emailTargetLoad?.id,
            })}
          </p>

          <Input
            label={t('loads.modal.emailAddressLabel')}
            type="email"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
            placeholder={t('loads.modal.emailAddressPlaceholder')}
            required
          />
        </div>
      </Modal>
    </>
  );
};

export default LoadDetailViewModal;
